import type { PrincipalDTO, TripViewDTO } from '@serviquim/contracts';
import Dexie, { type EntityTable } from 'dexie';

/** Sessão autenticada persistida localmente (sobrevive a reload/instalação do PWA). */
export interface SessionRecord {
  id: 1;
  tenantSlug: string;
  accessToken: string;
  refreshToken: string;
  principal: PrincipalDTO;
}

/**
 * Viagem em cache local — o que o app mostra quando está offline em campo.
 * Herda todos os campos do contrato (incl. `moeda`, `tipoLocomocao`, `odometroInicio`,
 * `odometroFim`, `kmRodado`); o Dexie persiste o objeto inteiro, então esses campos
 * novos entram no cache sem precisar de índice — nenhum bump de versão é necessário.
 */
export interface CachedTrip extends TripViewDTO {
  /** epoch ms da última sincronização bem-sucedida com o servidor. */
  cachedAt: number;
}

export type PendingExpenseStatus = 'pending' | 'syncing' | 'synced' | 'failed';

/** Item da fila de sincronização (outbox) — RF-VIA-04 / DoD 12. */
export interface PendingExpense {
  localId?: number;
  tripId: string;
  descricao: string;
  valor: string;
  /** Foto do comprovante comprimida em dataURL — some junto no sync como `fotoUrl`. */
  fotoDataUrl?: string;
  /** Gerada no dispositivo (crypto.randomUUID) — dedup no backend (RNF-14). */
  idempotencyKey: string;
  status: PendingExpenseStatus;
  createdAt: number;
  attempts: number;
  lastError?: string;
  syncedAt?: number;
}

class ViagensDatabase extends Dexie {
  session!: EntityTable<SessionRecord, 'id'>;
  trips!: EntityTable<CachedTrip, 'id'>;
  pendingExpenses!: EntityTable<PendingExpense, 'localId'>;

  constructor() {
    super('serviquim-viagens');
    this.version(1).stores({
      session: 'id',
      trips: 'id, status',
      pendingExpenses: '++localId, tripId, &idempotencyKey, status, createdAt',
    });
    // v2: indexa `cachedAt` (a lista ordena por ele — `orderBy('cachedAt')`). Sem o índice,
    // o Dexie lança "KeyPath cachedAt not indexed". Bump de versão migra o DB local existente.
    this.version(2).stores({
      session: 'id',
      trips: 'id, status, cachedAt',
      pendingExpenses: '++localId, tripId, &idempotencyKey, status, createdAt',
    });
  }
}

export const db = new ViagensDatabase();
